import { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { api, ApiError } from "./api/client";
import logo from "./assets/elezaby-logo.png";
import { SettingsMenu } from "./components/SettingsMenu";
import { ThemeToggle } from "./components/ThemeToggle";
import { FilterProvider } from "./lib/filters";
import { ui } from "./lib/ui";
import { useAsync } from "./lib/useAsync";
import { Dashboard } from "./pages/Dashboard";
import { Database } from "./pages/Database";
import { Fleet } from "./pages/Fleet";
import { Heatmap } from "./pages/Heatmap";
import { Login } from "./pages/Login";
import { Logs } from "./pages/Logs";
import { Releases } from "./pages/Releases";
import { Runs } from "./pages/Runs";
import { Store } from "./pages/Store";
import type { User, VersionInfo } from "./types";

/* One console, two front doors. A branch PC is one store at a time: its own
   numbers, its own floor plan, cameras and staff, its own runs — it carries
   no list of other branches, only the few stores it has been (Settings ›
   Switch store). The hub looks at every store's numbers and runs the fleet;
   nothing is processed there. */
const BRANCH_NAV = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/heatmap", label: "Heatmap" },
  { to: "/store", label: "Store" },
  { to: "/runs", label: "Runs" },
];
const HUB_NAV = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/heatmap", label: "Heatmap" },
  { to: "/fleet", label: "Fleet" },
  { to: "/releases", label: "Releases" },
];

const DEV_VERSION: VersionInfo = {
  version: null, mode: "branch", hub_url: null, branch: null, hub: null,
};

function Header({
  info,
  user,
  onSignOut,
  onStoreChanged,
}: {
  info: VersionInfo;
  user: User | null;
  onSignOut: () => void;
  onStoreChanged: () => void;
}) {
  const hub = info.mode === "hub";
  const nav = hub ? HUB_NAV : BRANCH_NAV;
  const subtitle = hub
    ? "Hub · all branches"
    : info.branch
      ? info.branch.placeholder
        ? "This branch · not named yet"
        : info.branch.local
          ? `${info.branch.name} · local store`
          : `${info.branch.name} · ${info.branch.governorate}`
      : "No store yet";
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-surface/90 backdrop-blur">
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-x-6 gap-y-2 px-6 py-3">
        <div className="flex items-center gap-3.5">
          {/* The two-tone brand logo is navy-on-transparent; a white plate
              keeps it legible on the dark theme. */}
          <span className="inline-flex items-center dark:rounded-lg dark:bg-white dark:px-2 dark:py-1">
            <img src={logo} alt="El Ezaby Pharmacy" className="h-8 w-auto" />
          </span>
          <div className="border-l border-border pl-3.5 leading-tight">
            <div className="font-semibold">Retail Analytics</div>
            <div className="text-xs text-muted" title={info.hub?.url ?? undefined}>
              {subtitle}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <nav className="flex gap-1">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-lg px-3.5 py-2 font-medium no-underline transition-colors duration-100 ${
                    isActive
                      ? "bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] font-semibold text-accent"
                      : "text-muted hover:bg-bg hover:text-text"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <SettingsMenu
            info={info}
            user={user}
            onSignOut={onSignOut}
            onStoreChanged={onStoreChanged}
          />
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
}

/** On a branch PC: the state of its link to the hub, loudly when it has
 *  none. Everything still works locally; nothing leaves the PC and no update
 *  arrives until it is connected. */
function HubAlert({ info }: { info: VersionInfo }) {
  if (info.mode !== "branch" || !info.hub || info.hub.connected) return null;
  // A local store is meant to stay on this PC: nothing to alert about.
  if (info.branch?.local) return null;
  const { url, error, registered_at } = info.hub;
  let text: string;
  let tone: string = ui.noticeError;
  if (!info.branch) {
    text = "This PC has no store yet. Open the Settings menu (the gear) › Switch store › Add new store to name one.";
    tone = "";
  } else if (!url) {
    text =
      "This branch is not connected to the hub. Its counts stay on this PC and it takes no updates. " +
      "To connect it, get a token for this store from the hub's Fleet page and re-run the installer " +
      "(or put the hub address in C:\\ProgramData\\Footfall\\footfall.env and the token in hub.token, then restart the service). " +
      "To work offline on purpose, give the store a name from Settings › Switch store › Add new store.";
  } else if (info.branch.placeholder && !error && !registered_at) {
    text = `This PC reports to ${url} but has no store yet. Paste the store's token from the hub's Fleet page under Settings › Switch store › Add new store.`;
  } else if (error) {
    text = `Cannot reach the hub at ${url}: ${error}. Syncing resumes on its own once it can; a revoked token needs a new one from the hub's Fleet page (Settings › Switch store › Add new store).`;
  } else if (!registered_at) {
    text = `Connecting to the hub at ${url}…`;
    tone = "";
  } else {
    return null;
  }
  return (
    <div className="mx-auto max-w-[1200px] px-6 pt-4">
      <p className={`${ui.notice} ${tone} mb-0`} role="alert">
        {text}
      </p>
    </div>
  );
}

/** Which release this PC runs — the answer to "what version is branch X on?"
 *  without opening its Apps list. Nothing to show in a dev checkout, where
 *  the API reports null. */
function VersionFooter({ info }: { info: VersionInfo }) {
  if (!info.version) return null;
  return (
    <footer className="mx-auto max-w-[1200px] px-6 pb-10 text-xs text-muted">
      El Ezaby Retail Analytics · version {info.version}
      {info.mode === "branch" && info.hub?.url ? ` · reporting to ${info.hub.url}` : ""}
    </footer>
  );
}

export function App() {
  // /api/version is public on both ends and decides everything else: the
  // navigation, whether a sign-in is needed, which store this is.
  const version = useAsync(() => api.version(), []);
  const info = version.data ?? DEV_VERSION;
  const hub = info.mode === "hub";

  // Hub only: the admin session. `undefined` = not checked yet.
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    if (!version.data) return;
    if (!hub) {
      setUser(null);
      return;
    }
    api
      .me()
      .then(setUser)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) setUser(null);
        else throw err;
      });
  }, [version.data, hub]);

  // A branch PC's link to the hub changes on its own (it connects, a token
  // is revoked): keep the alert honest without a reload.
  useEffect(() => {
    if (!version.data || hub) return;
    const timer = setInterval(version.reload, 60_000);
    return () => clearInterval(timer);
  }, [version.data, hub, version.reload]);

  const signOut = useCallback(() => {
    api.logout().finally(() => setUser(null));
  }, []);

  // A store switch re-scopes everything: the filter context is keyed on the
  // store below, so re-reading /api/version remounts every page; the URL's
  // view state (a selected run, an open rack) belonged to the old store.
  const navigate = useNavigate();
  const location = useLocation();
  const storeChanged = useCallback(() => {
    navigate(location.pathname, { replace: true });
    version.reload();
  }, [navigate, location.pathname, version.reload]);

  if (version.error) {
    return (
      <p className={`${ui.notice} ${ui.noticeError} m-6`}>
        The service is not reachable: {version.error}
      </p>
    );
  }
  if (!version.data || (hub && user === undefined)) return null;
  if (hub && !user) return <Login onSignedIn={setUser} />;

  return (
    <FilterProvider key={info.branch?.id ?? ""} defaultBranch={hub ? null : info.branch}>
      <div className="min-h-screen">
        <Header
          info={info}
          user={user ?? null}
          onSignOut={signOut}
          onStoreChanged={storeChanged}
        />
        <HubAlert info={info} />

        <main className="mx-auto max-w-[1200px] px-6 pb-8 pt-6">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/heatmap" element={<Heatmap />} />
            {hub ? (
              <>
                <Route path="/fleet" element={<Fleet />} />
                <Route path="/releases" element={<Releases />} />
              </>
            ) : (
              <>
                <Route path="/store" element={<Store />} />
                <Route path="/runs" element={<Runs />} />
                <Route path="/database" element={<Database />} />
                <Route path="/logs" element={<Logs />} />
                <Route path="/branches" element={<Navigate to="/store" replace />} />
              </>
            )}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>

        <VersionFooter info={info} />
      </div>
    </FilterProvider>
  );
}
